/**
 * Prompt Enhancer Service
 * 
 * Calls the prompt enhancer API which:
 * 1. Enhances the prompt (creates positive and negative prompts)
 * 2. Writes enhanced prompts + image_url to Supabase
 * 3. Returns enhanced prompts for use in video generation
 * 
 * Note: This service only enhances prompts. Video generation must be triggered
 * separately by calling the video generation API with the enhanced prompts.
 */

import { PROMPT_ENHANCER_CONFIG, API_ENDPOINTS } from '../utils/constants';

export interface PromptEnhancerRequest {
  image_url: string; // Image URL (required)
  prompt: string; // Original prompt (required)
  asset_id: string; // Asset ID from Supabase (required for enhance-from-supabase endpoint)
}

export interface PromptEnhancerResponse {
  prompt: string; // Enhanced prompt (matches fal.ai and industry standard)
  negative_prompt: string; // Enhanced negative prompt
  image_url: string; // Image URL (same as input)
  video_job_id?: string; // Optional video job ID from prompt enhancer (if it starts generation)
}

/**
 * Enhance prompt using the prompt enhancer API
 * 
 * The prompt enhancer:
 * - Takes image_url and prompt
 * - Enhances the prompt (creates enhanced prompt and negative prompt)
 * - Writes enhanced prompts + image_url to Supabase
 * - Returns enhanced prompts for use in video generation
 * 
 * Note: This does NOT trigger video generation. You must call the video generation
 * API separately with the enhanced prompts.
 * 
 * @param request - Request with image_url and prompt
 * @returns Enhanced prompts (prompt, negative_prompt) and image_url
 */
export async function enhancePrompt(
  request: PromptEnhancerRequest,
  retryCount: number = 0
): Promise<PromptEnhancerResponse> {
  // Validate required fields - asset_id must be a valid Supabase asset ID
  if (!request.asset_id || !request.asset_id.trim()) {
    throw new Error('Asset ID is required for prompt enhancement (Supabase asset ID)');
  }
  
  // Validate asset_id format (Supabase uses UUID format)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(request.asset_id.trim())) {
    console.warn('⚠️ [PromptEnhancer] Asset ID does not match UUID format:', request.asset_id);
    // Don't fail - backend will validate, but log warning
  }
  
  const baseUrl = PROMPT_ENHANCER_CONFIG.BASE_URL;
  const endpoint = API_ENDPOINTS.PROMPT_ENHANCER.ENHANCE_AND_GENERATE;

  // Always send prompt in request body - backend should use this as fallback
  // if it fails to get prompt from Supabase asset record
  const requestBody = {
    image_url: request.image_url,
    prompt: request.prompt, // Always included - backend should use this if asset record doesn't have it
    asset_id: request.asset_id, // Required for enhance-from-supabase endpoint
  };

  const startTime = Date.now();
  
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.error('⏰ [PromptEnhancer] Request timeout reached, aborting...');
    controller.abort();
  }, PROMPT_ENHANCER_CONFIG.TIMEOUT);
  
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
      // Add keepalive for mobile browsers to prevent connection drops
      keepalive: true,
    });
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const elapsed = Date.now() - startTime;
      console.error('❌ [PromptEnhancer] Request timed out after', elapsed, 'ms (limit:', PROMPT_ENHANCER_CONFIG.TIMEOUT, 'ms)');
      
      // Retry on timeout if we haven't exceeded max retries
      if (retryCount < PROMPT_ENHANCER_CONFIG.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, PROMPT_ENHANCER_CONFIG.RETRY_DELAY));
        return enhancePrompt(request, retryCount + 1);
      }
      
      throw new Error(`Prompt enhancement timed out after ${PROMPT_ENHANCER_CONFIG.TIMEOUT / 1000} seconds. Please try again.`);
    }
    console.error('❌ [PromptEnhancer] Network error:', error);
    console.error('❌ [PromptEnhancer] Error name:', error.name);
    console.error('❌ [PromptEnhancer] Error message:', error.message);
    
    // Retry on network errors if we haven't exceeded max retries
    if (retryCount < PROMPT_ENHANCER_CONFIG.MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, PROMPT_ENHANCER_CONFIG.RETRY_DELAY));
      return enhancePrompt(request, retryCount + 1);
    }
    
    throw new Error(`Network error: ${error.message || 'Failed to connect to prompt enhancer'}`);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to enhance prompt and generate video' }));
    console.error('❌ [PromptEnhancer] Request failed:', error);
    
    // Handle 402 Payment Required (insufficient credits) - don't retry, throw structured error
    if (response.status === 402) {
      const creditError = {
        type: 'insufficient_credits' as const,
        message: error.message || error.error || 'Insufficient credits',
        current_credits: error.current_credits ?? 0,
        required_credits: error.required_credits ?? 100,
      };
      throw creditError;
    }
    
    // If error is about missing prompt in asset record, note that prompt is in request body
    const errorMessage = error.error || error.message || 'Failed to enhance prompt and generate video';
    if (errorMessage.toLowerCase().includes('prompt') && errorMessage.toLowerCase().includes('asset')) {
      console.warn('⚠️ [PromptEnhancer] Backend reported missing prompt in asset record, but prompt is included in request body');
    }
    
    // Retry on 5xx errors (server errors) if we haven't exceeded max retries
    if (response.status >= 500 && retryCount < PROMPT_ENHANCER_CONFIG.MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, PROMPT_ENHANCER_CONFIG.RETRY_DELAY));
      return enhancePrompt(request, retryCount + 1);
    }
    
    throw new Error(errorMessage);
  }

  const result = await response.json();
  
  // Handle different field names from backend (enhanced_prompt, prompt, positive_prompt)
  const enhancedPrompt = result.prompt || result.enhanced_prompt || result.positive_prompt;
  
  if (!enhancedPrompt) {
    console.error('❌ [PromptEnhancer] No prompt field found in response. Available fields:', Object.keys(result));
    console.error('❌ [PromptEnhancer] Full response:', result);
    throw new Error('Prompt enhancer did not return an enhanced prompt');
  }
  
  return {
    prompt: enhancedPrompt,
    negative_prompt: result.negative_prompt,
    image_url: result.image_url,
    video_job_id: result.video_job_id, // Include video_job_id if provided by backend
  };
}


